<script setup>
import DefaultButton from "@/components/global-themed/buttons/DefaultButton.vue";
import {useStore} from "vuex";
import {computed} from "vue";
import {useCancelFlow} from "@/composables/cancel-flow/useCancelFlow";

const props = defineProps({
  enableCancelFlow: {
    type: Boolean,
    default: false,
  },
  organisationId: {
    type: [String, Number],
    required: true,
  }
});

const store = useStore();

const showCalendly = () => {
  if (!window.Calendly) {
    window.open('https://calendly.com/hike-seo/catch-up-call', '_blank');
    return;
  }

  window.Calendly.initPopupWidget({url: 'https://calendly.com/hike-seo/catch-up-call'})
}
const websites = computed(() => store.getters['agencyCancellation/websitesWithPublishedChanges'])

const {launchCancelFlow} = useCancelFlow(props.organisationId);

const handleCancelClick = async () => {
  try {
    if (props.enableCancelFlow) {
      await launchCancelFlow();
      return;
    }
  } catch (error) {
    // Fallback to regular cancellation flow
    console.error(error);
  }

  store.dispatch('agencyCancellation/nextStep')
};

</script>

<template>
  <div class="tw-m-auto tw-max-w-default-container tw-rounded-xl tw-bg-white tw-p-8 tw-text-base tw-flex tw-gap-10 tw-flex-col">
    <div class="tw-flex tw-flex-col tw-gap-5">
      <div class="tw-flex tw-flex-col tw-gap-3">
        <div class="tw-font-bold tw-text-lg">
          Thinking about leaving? Let’s talk first.
        </div>

        <div>We understand things change. But if something isn’t working for you, there’s usually an easy fix – and our team is here to help.</div>

        <div>
          Book a quick call to chat with us - no pressure, just support.

          <DefaultButton
            class="tw-mt-3"
            @click="showCalendly"
          >
            Book now
          </DefaultButton>
        </div>
      </div>

      <div class="tw-flex tw-flex-col tw-gap-3">
        <div class="tw-font-bold tw-text-lg">
          Cancelling your account?
        </div>

        <div>
          If you cancel, all changes you’ve already made will remain live as long as the snippet is on your site. However, you won’t be able to update them again in the future.
        </div>

        <div>
          If you decide to come back later, you’ll need to start fresh with a brand-new account.
        </div>
      </div>

      <div
        v-if="websites.length > 0"
        class="tw-flex tw-flex-col tw-gap-3"
      >
        <div class="tw-font-bold tw-text-lg">
          Websites with the Onsite Optimizer installed:
        </div>

        <div class="tw-flex tw-flex-col">
          <span>Below, you'll find a list of websites with the Onsite Optimizer script still present. We strongly recommend downloading a CSV file of all SEO adjustments you've made to each site. This record is crucial for you or your client to manually reimplement these changes, if necessary.</span>
        </div>

        <div
          v-for="website in websites"
          :key="website.id"
          class="tw-ml-4 tw-flex tw-gap-3"
        >
          <span>{{ website.url }}</span>
          <span>({{ website.count }} Published {{ website.count === 1 ? 'change' : 'changes' }})</span>
          <a
            v-if="website.count > 0"
            class="tw-underline tw-cursor-pointer"
            @click.prevent="store.dispatch('onsiteEditor/fetchChangesCsv', website.id)"
          >Download CSV</a>
        </div>
      </div>
    </div>

    <div class="tw-flex tw-justify-between">
      <DefaultButton
        class="!tw-w-[189px]"
        @click="store.dispatch('agencyCancellation/previousStep')"
      >
        Go Back
      </DefaultButton>
      <div class="tw-flex tw-gap-3">
        <DefaultButton
          variant="danger"
          @click="handleCancelClick"
        >
          Cancel Account
        </DefaultButton>
      </div>
    </div>
  </div>
</template>
